import { cn } from "@/lib/utils";
import { CountMark } from "./CountMark";
import { tabChrome } from "@/components/ui/Tabs";
import { MaybeLink } from "./MaybeLink";

/**
 * The bar's height, which the Define sidebar pins itself beneath.
 *
 * The design leaves the bar to size itself and reads 58px for it. The sidebar
 * consumes this one number as a CSS variable, so its offset and the bar agree.
 */
export const STAGE_TABS_HEIGHT = 58;

/** What a strip draws: a step of a stage or a tab of a page, which read the same here. */
export interface StripEntry {
  id: string;
  label: string;
  built: boolean;
}

// The bar is `.figtabs`; each tab is drawn by the `strip` look in `Tabs`, which
// the page tabs share. The design's 60 stacking level sat above this app's
// fullscreen viewer and settings dialog, which are fixed at 40 and 50, so the
// bar sits at 10.
const BAR =
  "sticky top-0 z-10 -mx-8 flex gap-1.5 border-b-2 border-border bg-background px-[30px] pt-[10px]";
const CHIP =
  "inline-flex size-[19px] flex-none items-center justify-center rounded-full bg-surface-sunken text-[11px] font-bold text-subtle-foreground";
const CHIP_ON = "bg-primary text-white";

/**
 * A stage's steps, or a page's tabs, as a strip across the top of its screens.
 *
 * For steps the rail already lists the same ones; this repeats them where the eye
 * is, with a number that becomes a check once the step is done and the attorney has
 * moved on. A check on the open entry would say they are finished with the screen
 * they are looking at, so the open entry keeps its number.
 *
 * An entry with no address is drawn but disabled, as the rail draws it: it is
 * progress, not a destination. The address is what says so, rather than `built`,
 * because there are two ways to have none. The product may have no route for it
 * yet, or this matter may not have reached it, and the caller knows which it is.
 * Where each entry lives is the caller's too, since a step's route and a tab's
 * are written from different places.
 */
export function StageTabs<Entry extends StripEntry>({
  label,
  entries,
  activeId,
  done,
  hrefFor,
  counts = {},
}: {
  /** The strip's accessible name, since the shell can hold one strip per stepped stage. */
  label: string;
  entries: readonly Entry[];
  activeId: Entry["id"];
  done: Readonly<Partial<Record<string, boolean>>>;
  hrefFor: (entry: Entry) => string | undefined;
  /** The changes waiting on each entry, by its id, as the rail counts them. */
  counts?: Readonly<Partial<Record<string, number>>>;
}) {
  return (
    // A navigation between routes rather than a tablist: there is no panel for a
    // tab to control, and the tab role promises arrow-key movement that links do
    // not do. The open entry is the current page.
    <nav data-testid="stage-tabs" aria-label={label} className={BAR}>
      {entries.map((step, index) => {
        const active = step.id === activeId;
        const checked = step.built && done[step.id] === true && !active;
        const chip = (
          <span className={cn(CHIP, active && CHIP_ON)} aria-hidden>
            {checked ? "✓" : String(index + 1)}
          </span>
        );
        const to = hrefFor(step);
        return (
          <MaybeLink
            key={step.id}
            to={to}
            current={active}
            className={tabChrome("strip", {
              active,
              disabled: to === undefined,
            })}
          >
            {chip}
            {step.label}
            {(counts[step.id] ?? 0) > 0 && (
              <CountMark count={counts[step.id] ?? 0} />
            )}
          </MaybeLink>
        );
      })}
    </nav>
  );
}
